import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  providerResourceSettingsSchema,
  serviceBookingSettingsSchema,
  serviceBookingUpdateSchema,
  type ProviderResourceSettings,
  type ServiceBookingSettings,
  type ServiceBookingUpdate,
} from './provider-resource-contracts'
import {
  ageSchedulingPolicySchema,
  type AgeSchedulingPolicy,
  type SchedulingPolicyPayload,
} from './settings-contracts'

export type ProviderResourceRepository = {
  getResources: (tenantId: string) => Promise<ProviderResourceSettings>
  updateSchedulingPolicy?: (
    tenantId: string,
    expectedPolicyRevision: number,
    policy: SchedulingPolicyPayload
  ) => Promise<ProviderResourceSettings>
  updateAgeSchedulingPolicy?: (
    tenantId: string,
    expectedPolicyRevision: number,
    policy: AgeSchedulingPolicy
  ) => Promise<ProviderResourceSettings>
  getServiceBooking?: (tenantId: string) => Promise<ServiceBookingSettings>
  updateServiceBooking?: (
    tenantId: string,
    update: ServiceBookingUpdate
  ) => Promise<ServiceBookingSettings>
}

export function createProviderResourceRepository({
  getUser,
  baseUrl = getEnvironment().onboardingApiBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): ProviderResourceRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    getResources(tenantId) {
      return client.request('/v1/settings/provider-resources/read', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: providerResourceSettingsSchema,
      })
    },
    updateSchedulingPolicy(tenantId, expectedPolicyRevision, policy) {
      return client.request('/v1/settings/provider-resources/read', {
        method: 'PUT',
        body: { tenantUid: tenantId, expectedPolicyRevision, ...policy },
        responseSchema: providerResourceSettingsSchema,
      })
    },
    updateAgeSchedulingPolicy(tenantId, expectedPolicyRevision, policy) {
      const ageSchedulingPolicy = ageSchedulingPolicySchema.parse(policy)
      return client.request('/v1/settings/provider-resources/read', {
        method: 'PUT',
        body: {
          tenantUid: tenantId,
          expectedPolicyRevision,
          ageSchedulingPolicy,
        },
        responseSchema: providerResourceSettingsSchema,
      })
    },
    getServiceBooking(tenantId) {
      return client.request('/v1/settings/service-booking', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: serviceBookingSettingsSchema,
      })
    },
    updateServiceBooking(tenantId, update) {
      const parsed = serviceBookingUpdateSchema.parse(update)
      return client.request('/v1/settings/service-booking', {
        method: 'PUT',
        body: { tenantUid: tenantId, ...parsed },
        responseSchema: serviceBookingSettingsSchema,
      })
    },
  }
}
